/**
 * One work-buffer modifier per gsplat component, shared by every feature that needs one.
 *
 * `GSplatComponent.setWorkBufferModifier` takes a single modifier (the copy stage's
 * `gsplatModifyVS`), so two features setting their own would replace each other. Features
 * register named parts here instead (the scan's sky classifier, wind on a splat object); each
 * part is a complete modifier source (all three hooks) whose helper functions and uniforms use
 * the feature's own prefix. The registry renames each part's three hooks and installs one source
 * that calls them in a fixed order (by part key), so the result never depends on who came first.
 *
 * Installing or changing the composed source re-copies that component's splats once; parts keep
 * their own uniforms on the component (`setParameter`).
 */
import type * as pc from 'playcanvas';

export interface WorkBufferModifierPart {
    glsl: string;
    wgsl: string;
}

type ModifierComponent = pc.GSplatComponent & {
    setWorkBufferModifier?: (m: { glsl: string; wgsl: string } | null) => void;
};

const HOOKS = ['modifySplatCenter', 'modifySplatRotationScale', 'modifySplatColor'] as const;

const sanitize = (key: string) => key.replace(/[^A-Za-z0-9_]/g, '_');

/** Rename one part's three hook definitions to `<hook>_<key>`. */
function renameHooks(source: string, key: string): string {
    let out = source;
    for (const hook of HOOKS) out = out.replace(new RegExp(`\\b${hook}\\s*\\(`), `${hook}_${sanitize(key)}(`);
    return out;
}

/** One modifier source calling every part's hooks in order. A single part is used as is. */
export function composeWorkBufferModifier(parts: ReadonlyArray<{ key: string; part: WorkBufferModifierPart }>, lang: 'glsl' | 'wgsl'): string {
    if (parts.length === 1) return lang === 'glsl' ? parts[0].part.glsl : parts[0].part.wgsl;
    const sources = parts.map(({ key, part }) => renameHooks(lang === 'glsl' ? part.glsl : part.wgsl, key)).join('\n');
    const keys = parts.map(({ key }) => sanitize(key));
    if (lang === 'glsl') {
        return `${sources}
void modifySplatCenter(inout vec3 center) { ${keys.map((k) => `modifySplatCenter_${k}(center);`).join(' ')} }
void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) { ${keys.map((k) => `modifySplatRotationScale_${k}(originalCenter, modifiedCenter, rotation, scale);`).join(' ')} }
void modifySplatColor(vec3 center, inout vec4 color) { ${keys.map((k) => `modifySplatColor_${k}(center, color);`).join(' ')} }
`;
    }
    return `${sources}
fn modifySplatCenter(center: ptr<function, vec3f>) { ${keys.map((k) => `modifySplatCenter_${k}(center);`).join(' ')} }
fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) { ${keys.map((k) => `modifySplatRotationScale_${k}(originalCenter, modifiedCenter, rotation, scale);`).join(' ')} }
fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) { ${keys.map((k) => `modifySplatColor_${k}(center, color);`).join(' ')} }
`;
}

const registries = new WeakMap<object, Map<string, WorkBufferModifierPart>>();

/**
 * Add, replace (part) or remove (null) one feature's part on a component and install the
 * composed modifier. Returns false when the component can't take a modifier (non-unified).
 */
export function setWorkBufferModifierPart(component: pc.GSplatComponent, key: string, part: WorkBufferModifierPart | null): boolean {
    const gs = component as ModifierComponent;
    if (typeof gs.setWorkBufferModifier !== 'function') return false;
    let parts = registries.get(gs);
    if (!parts) {
        if (!part) return true;
        parts = new Map();
        registries.set(gs, parts);
    }
    const previous = parts.get(key);
    if (part) {
        if (previous && previous.glsl === part.glsl && previous.wgsl === part.wgsl) return true;
        parts.set(key, part);
    } else {
        if (!previous) return true;
        parts.delete(key);
    }
    // Code-unit order, not localeCompare: some locales (e.g. Estonian) sort "zz…" before "wind".
    const ordered = [...parts.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([k, p]) => ({ key: k, part: p }));
    if (!ordered.length) {
        gs.setWorkBufferModifier(null);
        registries.delete(gs);
        return true;
    }
    gs.setWorkBufferModifier({ glsl: composeWorkBufferModifier(ordered, 'glsl'), wgsl: composeWorkBufferModifier(ordered, 'wgsl') });
    return true;
}

/** The part keys installed on a component (diagnostics, tests). */
export function workBufferModifierParts(component: pc.GSplatComponent): string[] {
    return [...(registries.get(component)?.keys() ?? [])].sort();
}
